import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '../cn';

export interface TabItem<V extends string = string> {
  value: V;
  label: ReactNode;
  /** Optional panel content. Omit when the parent renders its own content from `value`. */
  content?: ReactNode | undefined;
  disabled?: boolean | undefined;
}

export interface TabsProps<V extends string = string> {
  items: readonly TabItem<V>[];
  /** Uncontrolled initial tab. */
  defaultValue?: V | undefined;
  /** Controlled tab. */
  value?: V | undefined;
  onChange?: ((value: V) => void) | undefined;
  'aria-label'?: string | undefined;
  className?: string | undefined;
}

/** Underline tabs on a single hairline (one of the four allowed dividers). Arrow keys move between tabs. */
export function Tabs<V extends string = string>({
  items,
  defaultValue,
  value,
  onChange,
  className,
  ...aria
}: TabsProps<V>) {
  const [inner, setInner] = useState<V | undefined>(defaultValue ?? items[0]?.value);
  const active = value ?? inner;
  const id = useId();
  const select = (v: V) => {
    setInner(v);
    onChange?.(v);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const enabled = items.filter((it) => !it.disabled);
    const cur = enabled.findIndex((it) => it.value === active);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? enabled.length - 1
          : (cur + (e.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length;
    const item = enabled[next];
    if (!item) return;
    select(item.value);
    document.getElementById(`${id}-tab-${item.value}`)?.focus();
  };
  const current = items.find((it) => it.value === active);
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div role="tablist" className="flex gap-1 border-b border-border-subdued" {...aria}>
        {items.map((it) => {
          const on = it.value === active;
          return (
            <button
              key={it.value}
              type="button"
              role="tab"
              id={`${id}-tab-${it.value}`}
              aria-selected={on}
              aria-controls={it.content !== undefined ? `${id}-panel-${it.value}` : undefined}
              tabIndex={on ? 0 : -1}
              disabled={it.disabled}
              onClick={() => {
                select(it.value);
              }}
              onKeyDown={onKeyDown}
              className={cn(
                'ds-body-sm mr-5 -mb-px cursor-pointer border-b-2 px-1 py-2.5 transition-colors duration-150',
                on
                  ? 'border-content-primary-default font-semibold text-content-primary-default'
                  : 'border-transparent text-content-subdued hover:text-content-default',
                'disabled:cursor-not-allowed disabled:text-content-disabled',
              )}
            >
              {it.label}
            </button>
          );
        })}
      </div>
      {current?.content !== undefined ? (
        <div
          role="tabpanel"
          id={`${id}-panel-${current.value}`}
          aria-labelledby={`${id}-tab-${current.value}`}
        >
          {current.content}
        </div>
      ) : null}
    </div>
  );
}
